import { SHStack, SHTypography } from "@components/design-systems";
import { RenderAnalysisSummaryItemProps } from "@models/platform-analysis/entities/steps/summary";
import { TableViewMode } from "@models/platform-analysis/enums/tableViewMode";
import {
  sortByProductOrder,
  sortFeatureAnalysisRowsByProductOrder,
} from "@pages/platform-analysis-v2/_id/utils";
import { summaryBusinessMetricAnalysisShortlistedTableRowsSelectors as rowSelectors } from "@redux/slices/platform-analysis";
import { RootState } from "@redux/store";
import { isEqual } from "lodash";
import { memo, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { SummaryAnalysisTable } from "../features/table-components/container";

export const SummaryBusinessMetrics = memo(
  ({
    isReview,
    name,
    userType,
    disabled: sectionDisabled,
  }: RenderAnalysisSummaryItemProps) => {
    const storedSelectedProducts = useSelector(
      (state: RootState) =>
        state.platformAnalysis?.platformAnalysis?.summary
          ?.businessMetricAnalysis?.shortlisted?.selectedProducts || [],
    );
    const storedTableRows = useSelector(rowSelectors.selectAll);
    const storedCurrentProductRecommendations = useSelector(
      (state: RootState) =>
        state.platformAnalysis?.internal?.summary?.productRecommendations,
    );

    const [productIdsOrder, setProductIdsOrder] = useState<string[]>(
      Object.keys(storedCurrentProductRecommendations || {}),
    );

    const orderedSelectedProducts = useMemo(() => {
      if (!isReview) return storedSelectedProducts;

      const sortedData = sortByProductOrder({
        data: storedSelectedProducts,
        productRecommendations: storedCurrentProductRecommendations,
        getItemId: (item) => item.id,
      });

      const newIds = sortedData.map((item) => item.id);
      if (!isEqual(productIdsOrder, newIds)) setProductIdsOrder(newIds);

      return sortedData;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isReview, storedSelectedProducts, storedCurrentProductRecommendations]);

    const orderedRows = useMemo(() => {
      if (!isReview) return storedTableRows;

      return sortFeatureAnalysisRowsByProductOrder({
        originalRows: storedTableRows,
        productRecommendations: storedCurrentProductRecommendations,
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [storedTableRows, productIdsOrder, isReview]);

    return (
      <SHStack gap={2}>
        <SHStack>
          <SHTypography disabled={sectionDisabled} variant="subtitle2">
            {name?.[userType]}
          </SHTypography>
        </SHStack>

        <SummaryAnalysisTable
          isReview={isReview}
          rows={orderedRows}
          selectedProducts={orderedSelectedProducts}
          wrapperProps={{
            noSelectionColumn: true,
            showAnalysisScore: true,
            showFeatureTooltip: false,
            viewMode: TableViewMode.Detail,
          }}
        />
      </SHStack>
    );
  },
);
